import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    if (this.dataset.variantSelection !== 'true') {
      requestAnimationFrame(this.#showProductFeaturedMedia);
    }
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Shows the product featured media on the initial page load.
   * Variant-triggered gallery replacements keep the selected variant media first.
   */
  #showProductFeaturedMedia = () => {
    const featuredImageUrl = document.querySelector('meta[property="og:image"]')?.getAttribute('content');
    const slides = Array.from(this.slideshow?.slides ?? []);

    if (!featuredImageUrl || !slides?.length) return;

    const featuredImagePath = this.#getImagePath(featuredImageUrl);
    const featuredSlideIndex = slides.findIndex((slide) => {
      const image = slide.querySelector('.product-media__image');
      return image instanceof HTMLImageElement && this.#getImagePath(image.currentSrc || image.src) === featuredImagePath;
    });

    if (featuredSlideIndex <= 0) return;

    this.slideshow?.select(featuredSlideIndex, undefined, { animate: false });

    const featuredMediaId = slides[featuredSlideIndex]?.querySelector('.product-media')?.getAttribute('data-media-id');
    const grid = this.querySelector('.media-gallery__grid');
    const featuredGridItem = featuredMediaId
      ? Array.from(grid?.children ?? []).find(
          (item) => item.querySelector('.product-media')?.getAttribute('data-media-id') === featuredMediaId
        )
      : null;

    if (grid && featuredGridItem) {
      grid.prepend(featuredGridItem);
    }
  };

  /**
   * Normalizes a responsive Shopify image URL for reliable comparison.
   *
   * @param {string} imageUrl - The image URL.
   * @returns {string} The host and path without resizing query parameters.
   */
  #getImagePath(imageUrl) {
    try {
      const url = new URL(imageUrl, window.location.origin);
      return `${url.host}${url.pathname}`;
    } catch {
      return imageUrl.split('?')[0];
    }
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    newMediaGallery.dataset.variantSelection = 'true';
    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   *
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
